import classNames from 'classnames';
import { HOUR_MINUTES_POSTFIXED_DATE_FORMAT } from 'constants/datetime';
import { sum } from 'lodash-es';
import memoizeOne from 'memoize-one';
import React, { useMemo } from 'react';
import { percentOf as calculatePercentage } from 'utils/numbers';
import type { DataTableTooltipDataItem, TableDataValue } from './DataTableTooltip';
import DataTableTooltip from './DataTableTooltip';
import styles from './DurationDistributionBar.module.scss';
import type { DurationValueTypes } from './DurationFormat';
import DurationFormat from './DurationFormat';

interface BarType {
  className?: string;
  width: number;
  variant?: ValueVariants;
  item?: DurationDistributionDataValue;
}

const Bar = ({ item, variant, width, className }: BarType) => {
  const style: React.CSSProperties = { width: `${width}%` };

  if (width === 0) {
    style.borderLeft = 'unset';
  }

  return (
    <div
      className={classNames(styles.value, styles[`variant-${variant || item?.variant}`], className)}
      style={{ width: `${width}%` }}
    />
  );
};

type ValueVariants =
  | 'billable'
  | 'non-billable'
  | 'planned'
  | 'tasked'
  | 'over-capacity'
  | 'over-planned-capacity';

export interface DurationDistributionDataValue {
  label: string;
  value: number;
  variant: ValueVariants;
  valueType?: DurationValueTypes;
}

export interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'size'> {
  /** Height of the element? Defaults to 20px if unspecified */
  height?: number;
  /** Max value, typically the "total capacity" */
  maxValue?: number;
  /** Individual "duration values", typically related to billable-, non-billable-, estimated-duration */
  values?: Array<DurationDistributionDataValue>;
  /** Sizing of progressbar? */
  size?: 'small' | 'medium' | 'large';
  /** Just want the progressbar?  */
  disableTooltip?: boolean;
  /** Customise the title of the tooltip  */
  tooltipTitle?: string;
  defaultValueType?: DurationValueTypes;
}

function getSecondaryValue(value: number) {
  if (value % 1 !== 0) {
    return `~${Math.floor(value)}%`;
  }
  return `${value}%`;
}

const DurationDistributionBar = (props: Props) => {
  const {
    className,
    style,
    values = [],
    maxValue = 0,
    size = 'medium',
    disableTooltip = false,
    height,
    tooltipTitle = 'Capacity summary',
    defaultValueType = 'hours',
    ...rest
  } = props;

  const totalOfLoggedTimeValues = useMemo(
    () =>
      sum(
        values
          .filter(value => ['billable', 'non-billable'].includes(value.variant))
          .map(v => v.value)
      ),
    [values]
  );
  const totalOfPlannedTimeValues = useMemo(
    () => sum(values.filter(value => value.variant === 'planned').map(v => v.value)),
    [values]
  );
  const totalOfTaskedTimeValues = useMemo(
    () => sum(values.filter(value => value.variant === 'tasked').map(v => v.value)),
    [values]
  );
  const loggedTimeOverMax =
    totalOfLoggedTimeValues > maxValue ? totalOfLoggedTimeValues - maxValue : 0;

  const totalOfScheduledTimeValues = totalOfPlannedTimeValues + totalOfTaskedTimeValues;
  const scheduledTimeOverMax =
    loggedTimeOverMax > 0
      ? totalOfScheduledTimeValues
      : totalOfScheduledTimeValues + totalOfLoggedTimeValues > maxValue
        ? totalOfScheduledTimeValues + totalOfLoggedTimeValues - maxValue
        : 0;

  const remainingLoggedCapacity = maxValue - totalOfLoggedTimeValues;
  const remainingScheduledCapacity =
    Math.max(0, remainingLoggedCapacity) - totalOfScheduledTimeValues;

  const valuesToTooltipData = useMemo(
    () =>
      memoizeOne(() => {
        const loggedValues = values.filter(v => ['billable', 'non-billable'].includes(v.variant));
        const valuesToReturn: Array<TableDataValue> = [
          {
            label: 'Total capacity',
            value: (
              <DurationFormat
                value={maxValue}
                valueType={defaultValueType}
                format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
              />
            ),
            align: 'right',
          },
          'divider',
          {
            label: 'Logged time',
            value: (
              <DurationFormat
                valueType={defaultValueType}
                value={totalOfLoggedTimeValues}
                format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
              />
            ),
            align: 'right',
            bulletVariant: 'success',
            secondaryValue: getSecondaryValue(
              calculatePercentage(sum(loggedValues.map(v => v.value)), maxValue)
            ),
            subValues:
              loggedValues.length > 1 || remainingLoggedCapacity < 0
                ? loggedValues.map(
                    (v): DataTableTooltipDataItem => ({
                      label: v.label,
                      value: (
                        <DurationFormat
                          valueType={v.valueType ?? 'hours'}
                          value={v.value}
                          format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                        />
                      ),
                      secondaryValue: getSecondaryValue(calculatePercentage(v.value, maxValue)),
                      align: 'right',
                    })
                  )
                : undefined,
          },
        ];

        valuesToReturn.push(
          ...values
            .filter(v => !(v.variant === 'billable' || v.variant === 'non-billable'))
            .map(
              (v): TableDataValue => ({
                label: v.label,
                value: (
                  <DurationFormat
                    valueType={v.valueType ?? 'hours'}
                    value={v.value}
                    format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
                  />
                ),
                bulletVariant: 'success',
                bulletPattern: v.variant.includes('billable') ? 'solid' : 'dashed',
                align: 'right',
                secondaryValue: getSecondaryValue(calculatePercentage(v.value, maxValue)),
              })
            )
        );

        valuesToReturn.push('divider');

        valuesToReturn.push({
          label: remainingLoggedCapacity >= 0 ? 'Free capacity' : 'Overtime',
          value: (
            <DurationFormat
              valueType={defaultValueType}
              value={
                remainingLoggedCapacity >= 0
                  ? Math.max(0, remainingScheduledCapacity) // Free capacity mode
                  : Math.abs(remainingLoggedCapacity) // Overtime mode
              }
              format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
            />
          ),
          secondaryValue: getSecondaryValue(
            Math.floor(Math.abs(calculatePercentage(remainingLoggedCapacity, maxValue)))
          ),
          align: 'right',
          bulletVariant: remainingLoggedCapacity > 0 ? 'neutral' : 'info',
        });

        if (
          remainingScheduledCapacity !== remainingLoggedCapacity &&
          remainingScheduledCapacity < 0
        ) {
          valuesToReturn.push({
            label: 'Over-scheduling',
            value: (
              <DurationFormat
                valueType={defaultValueType}
                value={Math.abs(remainingScheduledCapacity)} // Always positive number
                format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
              />
            ),
            secondaryValue: getSecondaryValue(
              Math.floor(Math.abs(calculatePercentage(remainingScheduledCapacity, maxValue)))
            ),
            align: 'right',
            bulletVariant: 'info',
            bulletPattern: 'dashed',
          });
        }

        return valuesToReturn;
      }),
    [
      values,
      defaultValueType,
      totalOfLoggedTimeValues,
      maxValue,
      remainingLoggedCapacity,
      remainingScheduledCapacity,
    ]
  );

  return (
    <DataTableTooltip
      disabled={disableTooltip}
      title={tooltipTitle}
      dataValues={valuesToTooltipData}
    >
      <div
        {...rest}
        className={classNames(styles.container, styles[`size-${size}`], className)}
        style={{ height, ...style }}
      >
        <div className={styles.innerContainer}>
          {totalOfLoggedTimeValues > 0 && (
            <Bar
              variant="billable"
              width={calculatePercentage(totalOfLoggedTimeValues - loggedTimeOverMax, maxValue)}
            />
          )}
          {loggedTimeOverMax > 0 && (
            <Bar variant="over-capacity" width={calculatePercentage(loggedTimeOverMax, maxValue)} />
          )}
          {totalOfScheduledTimeValues > 0 && (
            <Bar
              variant="planned"
              width={calculatePercentage(
                totalOfScheduledTimeValues - scheduledTimeOverMax,
                maxValue
              )}
            />
          )}
          {scheduledTimeOverMax > 0 && (
            <Bar
              variant="over-planned-capacity"
              width={calculatePercentage(scheduledTimeOverMax, maxValue)}
            />
          )}
        </div>
      </div>
    </DataTableTooltip>
  );
};

export default DurationDistributionBar;
